import { Skeleton } from "@/components/ui/skeleton";
import {
  useGetSingRequest,
  type Datum,
  type LastedSignedDocument,
} from "@/queries/process/signature.api";
import {
  filterTypesEnum,
  sortDirectionEnum,
  type CurrentFiltersType,
  type FilterTypeList,
  type MultiEntitySelections,
  type SortDirectionType,
} from "@/types/common/filters";
import { useMemo, useState } from "react";
import { useParams } from "react-router";
import SignDocumentTimeline from "./timelines/sign-document-timeline";
import SortDropdown from "@/pages/procesos/components/SortDropdown";
import Filter from "@/components/ui/filters";

const FilterFields: FilterTypeList[] = [
  {
    label: "Estado",
    type: filterTypesEnum.status,
    field: "status",
    options: [
      { label: "Exitosa", field: "signed" },
      { label: "Fallida", field: "failed" },
    ],
  },
];

const SignRequestHistory = () => {
  const params = useParams<{ procedureId: string; singRequestId: string }>();
  const [filters, setFilters] = useState<CurrentFiltersType[]>([]);
  const [selectedEntities, setSelectedEntities] =
    useState<MultiEntitySelections>({});
  const [selectedStatus, setSelectedStatus] = useState<
    "signed" | "failed" | undefined
  >(undefined);
  const [orderDirection, setOrderDirection] = useState<SortDirectionType>(
    sortDirectionEnum.desc,
  );

  const {
    data: signDocuments,
    isLoading,
    isError,
  } = useGetSingRequest(
    params.procedureId!,
    {
      relationships: [
        "nodeExecution",
        "signers",
        "requiredSigners",
        "optionalSigners",
        "signedBy",
        "signedDocuments",
      ],
    },
    5000,
  );

  const getSameVersionSigned = (
    documents: Datum[],
    version: number,
    date: Date,
  ) => {
    return documents.find(
      (d) =>
        d.status === "signed" &&
        d.version === version &&
        new Date(d.created_at) < new Date(date),
    );
  };

  const logs: LastedSignedDocument[] = useMemo(() => {
    if (!signDocuments?.data) return [];

    return signDocuments.data.flatMap((signDocument) => {
      const signerMap = new Map(
        signDocument.signers.data.map((s) => [s.id, s]),
      );

      return signDocument.signedDocuments.data.map((signed) => {
        const lastVersionSigned = getSameVersionSigned(
          signDocument.signedDocuments.data,
          signed?.version ?? 1,
          signed.created_at,
        );

        return {
          ...signed,
          file:
            signed.status === "signed"
              ? signed?.file
              : lastVersionSigned
                ? lastVersionSigned
                : signDocuments.data.find(
                    (d) => d.id === signed.sign_request_id,
                  ),
          signer: signerMap.get(signed.signed_by) || null,
        };
      });
    }) as unknown as LastedSignedDocument[];
  }, [signDocuments]);

  const filteredLogs: LastedSignedDocument[] = useMemo(() => {
    if (!Array.isArray(logs) || logs.length === 0) return [];

    let result = [...logs];

    const statusSelections = filters.length
      ? ((filters[0]["status"] as unknown as string[] | undefined) ?? [])
      : [];

    if (statusSelections.length > 0) {
      result = result.filter((item) =>
        statusSelections.includes(item.status as string),
      );
    }

    return result;
  }, [logs, filters]);

  const { recentItems, yesterdayItems, pastItems } = useMemo(() => {
    const items = Array.isArray(filteredLogs) ? filteredLogs : [];

    const now = new Date();
    const todayStart = new Date(now);
    todayStart.setHours(0, 0, 0, 0);

    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(todayStart.getDate() - 1);

    const recent: LastedSignedDocument[] = [];
    const yesterday: LastedSignedDocument[] = [];
    const past: LastedSignedDocument[] = [];

    const getDate = (rev: LastedSignedDocument): Date => {
      const raw = rev.updated_at ?? rev.created_at ?? null;
      const d =
        raw instanceof Date ? raw : raw ? new Date(String(raw)) : new Date();
      return d;
    };

    items.forEach((rev) => {
      const d = getDate(rev);
      if (d >= todayStart) {
        recent.push(rev);
      } else if (d >= yesterdayStart && d < todayStart) {
        yesterday.push(rev);
      } else {
        past.push(rev);
      }
    });

    const sortGroup = (arr: LastedSignedDocument[]) =>
      [...arr].sort((a, b) => {
        const aIsSelected =
          selectedStatus != null && a.status === selectedStatus;
        const bIsSelected =
          selectedStatus != null && b.status === selectedStatus;

        if (selectedStatus && aIsSelected !== bIsSelected) {
          return aIsSelected ? -1 : 1;
        }

        const da = getDate(a).getTime();
        const db = getDate(b).getTime();

        return orderDirection === sortDirectionEnum.asc ? da - db : db - da;
      });

    return {
      recentItems: sortGroup(recent),
      yesterdayItems: sortGroup(yesterday),
      pastItems: sortGroup(past),
    };
  }, [filteredLogs, selectedStatus, orderDirection]);

  const orderedBlocks = useMemo(() => {
    const blocks = [
      { title: "Recientemente", items: recentItems },
      { title: "Ayer", items: yesterdayItems },
      { title: "Pasados", items: pastItems },
    ];

    return orderDirection === sortDirectionEnum.asc
      ? [...blocks].reverse()
      : blocks;
  }, [recentItems, yesterdayItems, pastItems, orderDirection]);

  const hasAnyItems = useMemo(
    () => orderedBlocks.some((b) => (b.items?.length ?? 0) > 0),
    [orderedBlocks],
  );

  const handleApplySort = (
    field: string | undefined,
    direction: SortDirectionType,
  ) => {
    setOrderDirection(direction);

    if (!field || field === "ALL") {
      setSelectedStatus(undefined);
      return;
    }

    if (field === "signed" || field === "failed") {
      setSelectedStatus(field);
    }
  };

  console.log("orderedBlocks", orderedBlocks);

  return (
    <>
      <div className="flex justify-between pb-6" data-testid="process-history">
        <div className="flex space-x-3">
          <SortDropdown
            currentOrderField={selectedStatus ?? "ALL"}
            currentOrderDirection={orderDirection}
            sortOptions={[]}
            onClick={handleApplySort}
          />
          <Filter
            filters={FilterFields}
            currentFilters={filters}
            onCurrentFiltersChange={setFilters}
            selecteds={selectedEntities}
            onSetSelecteds={setSelectedEntities}
          />
        </div>
      </div>
      <div className="bg-neutral-object border border-neutral-quiet rounded p-4 h-[calc(100vh-300px)] overflow-auto">
        {isLoading ? (
          <div role="status" className="max-w-sm animate-pulse">
            <Skeleton className="h-3 rounded-full w-48 mb-4" />
            <Skeleton className="h-2 rounded-full max-w-[380px] mb-2.5" />
            <Skeleton className="h-2 rounded-full max-w-[340px] mb-2.5" />
            <Skeleton className="h-2 rounded-full max-w-[320px] mb-2.5" />
          </div>
        ) : isError ? (
          <p className="text-neutral-muted h-full flex items-center justify-center text-danger-quiet">
            Ha ocurrido un error al cargar las solicitudes de firma.
          </p>
        ) : hasAnyItems ? (
          <div className="space-y-10">
            {orderedBlocks.map((block) =>
              (block.items?.length ?? 0) > 0 ? (
                <div key={block.title}>
                  <SignDocumentTimeline
                    title={block.title}
                    items={block.items}
                  />
                </div>
              ) : null,
            )}
          </div>
        ) : (
          <p className="text-neutral-muted h-full flex items-center justify-center text-body">
            No hay solicitudes de firma.
          </p>
        )}
      </div>
    </>
  );
};

export default SignRequestHistory;
